import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as XLSX from 'xlsx';
import axiosInstance, { checkTokenValidity, CheckUserExpiry, getUserDetails, canEditSamplingFishData } from "../../components/content/axiosConfig";
import { resolveScopedFisherID } from "../../utils/pondListForUser";
import Swal from 'sweetalert2';
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import CustomModal from "../../components/custom/customModal";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import PondDetailWorkflowTabs from "../../components/custom/PondDetailWorkflowTabs";
import { useHistory } from "react-router-dom";
import { imageFieldToDataUrl } from "../../utils/imageFieldToDataUrl";
import {
    CAPTURE_METHOD_OPTIONS,
    normalizeCaptureMethod,
    normalizeSamplingMethod,
    mapSamplingMethodToCaptureMethod,
    getFishAssignmentMode,
    getFishAssignmentColumnLabel,
    getExcelImportColumnSpec,
    validateExcelRequiredColumns,
    filterImportRows,
    EXCEL_EXAMPLE_ROW_MARKER,
    buildFishSamplePayload,
} from "../../utils/samplingCpueUtils";
import { displayMethodLabel, WrLabel, WsLabel } from "../../utils/samplingDisplayLabels";

const getRawCaptureMethodFromRow = (row) => {
    if (!row || typeof row !== "object") return "";
    const v =
        row.CaptureMethod ??
        row["Capture Method"] ??
        row.captureMethod ??
        row.CAPTURE_METHOD;
    if (v === undefined || v === null) return "";
    return String(v).trim();
};

/** Returns { ok, value } where value is a canonical option or null if cell empty. */
const normalizeCaptureMethodToken = (raw) => {
    if (raw === "" || raw === undefined || raw === null) {
        return { ok: true, value: null };
    }
    const str = String(raw).trim();
    if (!str) return { ok: true, value: null };
    const normalized = normalizeCaptureMethod(str);
    if (CAPTURE_METHOD_OPTIONS.includes(normalized)) {
        return { ok: true, value: normalized };
    }
    return {
        ok: false,
        value: null,
        message: `Invalid CaptureMethod "${str}"`,
    };
};

const resolveCaptureMethodForRow = (row, pond) => {
    const raw = getRawCaptureMethodFromRow(row);
    const n = normalizeCaptureMethodToken(raw);
    if (!n.ok) return { error: n.message };
    if (n.value) return { value: n.value };
    const fromPond = mapSamplingMethodToCaptureMethod(pond?.SamplingMethod);
    if (fromPond) return { value: fromPond };
    return { value: "Angling" };
};

const getEffortUnitFromRow = (row) => {
    if (!row || typeof row !== "object") return "";
    const v =
        row.EffortUnit ??
        row["Effort Unit"] ??
        row.effortUnit ??
        row.Area ??
        row.Net ??
        row.Haul;
    if (v === undefined || v === null) return "";
    return String(v).trim();
};

const resolveEffortUnitForRow = (row, effortUnits) => {
    const raw = getEffortUnitFromRow(row);
    if (!raw) return { value: null };

    const units = [...(effortUnits || [])].sort(
        (a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0)
    );
    const exact = units.find(
        (unit) => String(unit.Label || "").trim().toLowerCase() === raw.toLowerCase()
    );
    if (exact?.id != null) return { value: exact.id };

    const num = parseInt(raw, 10);
    if (Number.isFinite(num) && num >= 1 && num <= units.length) {
        return { value: units[num - 1].id };
    }

    return {
        error: `Invalid EffortUnit "${raw}". Use a label from the Effort Units sheet (e.g. ${units[0]?.Label || "Area 1"}).`,
    };
};

const getAnglerFromRow = (row) => {
    if (!row || typeof row !== "object") return "";
    const v = row.Angler ?? row["Angler Name"] ?? row.angler ?? row.AnglerStaff;
    if (v === undefined || v === null) return "";
    return String(v).trim();
};

const resolveAnglerForRow = (row, pondAnglers) => {
    const raw = getAnglerFromRow(row);
    if (!raw) return { value: null };
    const match = (pondAnglers || []).find(
        (a) => String(a.fullName || "").trim().toLowerCase() === raw.toLowerCase()
    );
    if (match?.staffId != null) return { value: match.staffId };
    const num = parseInt(raw, 10);
    if (Number.isFinite(num) && (pondAnglers || []).some((a) => String(a.staffId) === String(num))) {
        return { value: num };
    }
    return {
        error: `Invalid Angler "${raw}". Use a name from the Anglers sheet.`,
    };
};

function getSamplingDataWorksheet(workbook) {
    if (!workbook?.SheetNames?.length) return null;
    const names = workbook.SheetNames;
    const idx = names.findIndex(
        (n) => String(n).trim().toLowerCase() === "sampling data"
    );
    const sheetName = idx >= 0 ? names[idx] : names[0];
    return workbook.Sheets[sheetName] || null;
}

const IMPORT_PREVIEW_ROW_LIMIT = 25;
const IMPORT_BATCH_SIZE = 100;
const IMPORT_PROGRESS_YIELD_EVERY = 50;

const yieldToUi = () => new Promise((resolve) => setTimeout(resolve, 0));

const SamplingDetailViaPondDetail = () => {
    const [samples, setSamples] = useState([]);
    const history = useHistory();
    const [pond, setPond] = useState({});
    const [availableFishes, setAvailableFishes] = useState([]);
    const [lengthUnit, setLengthUnit] = useState("");
    const [weightUnit, setWeightUnit] = useState("");
    const userDetails = getUserDetails();
    const canEditSampling = canEditSamplingFishData(userDetails);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage] = useState(30);
    const [isLoading, setIsLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [imageToShow, setImageToShow] = useState("");
    const { id } = useParams();
    const [selectedID, setSelectedID] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [selectedSample, setSelectedSample] = useState(null);
    const [effortUnits, setEffortUnits] = useState([]);
    const [pondAnglers, setPondAnglers] = useState([]);
    const [updateForm, setUpdateForm] = useState({
        length: "", weight: "", ws: "", wr: "", tagNumber: "",
        CaptureMethod: "Angling", EffortUnitID: "", AnglerStaffID: "", Status: "",
    });
    const assignmentMode = getFishAssignmentMode(pond?.SamplingMethod);
    const assignmentLabel = getFishAssignmentColumnLabel(pond?.SamplingMethod);
    const sessionCaptureMethod =
        mapSamplingMethodToCaptureMethod(pond?.SamplingMethod) || "Angling";
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importErrors, setImportErrors] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [previewTotalRows, setPreviewTotalRows] = useState(0);
    const [showPreview, setShowPreview] = useState(false);
    const [isParsingFile, setIsParsingFile] = useState(false);
    const [importProgress, setImportProgress] = useState({ current: 0, total: 0, currentRow: null });
    const [processedRows, setProcessedRows] = useState([]);

    const FisherID = userDetails.FisherID;

    const formatWrAsPercent = (wr) => {
        if (wr === "" || wr === undefined || wr === null) return "";
        const num = parseFloat(wr);
        if (Number.isNaN(num)) return "";
        return (num * 100).toFixed(1) + "%";
    };

    const CheckExpiry = useCallback(async () => {
        await CheckUserExpiry();
    }, []);

    const FetchSamples = useCallback(async () => {
        try {
            setIsLoading(true);
            const detailParams = { id };
            const sessionFisherID = resolveScopedFisherID(userDetails, pond);
            if (sessionFisherID != null && sessionFisherID !== "") {
                detailParams.FisherID = sessionFisherID;
            } else if (FisherID != null && FisherID !== "") {
                detailParams.FisherID = FisherID;
            }
            const pondResponse = await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
                params: detailParams
            });

            setPond(pondResponse.data);
            setEffortUnits(pondResponse.data.EffortUnits || []);
            setLengthUnit(pondResponse.data.LengthUnitName);
            setWeightUnit(pondResponse.data.WeightUnitName);

            if (getFishAssignmentMode(pondResponse.data.SamplingMethod) === "angler") {
                try {
                    const anglerRes = await axiosInstance.get("/api/PondDetail/PondAnglers", {
                        params: { PondDID: id },
                    });
                    setPondAnglers(anglerRes.data?.anglers || []);
                } catch {
                    setPondAnglers([]);
                }
            } else {
                setPondAnglers([]);
            }

            const fishResponse = await axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${id}`);
            setAvailableFishes(fishResponse.data);

            const response = await axiosInstance.get(`/api/FishSampling/GetAllSamplesViaPondDetail/${id}`, {
                params: { search: searchTerm }
            });
            setSamples(response.data);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            if (error.response?.status === 401) {
                localStorage.removeItem("user");
                localStorage.removeItem("token");
                window.location.reload();
                showErrorToast("Session expired. Please log in again.");
            } else {
                const errorMessage = error.response?.data?.message || "Failed to fetch sampling details";
                showErrorToast(errorMessage);
            }
            console.error("Error fetching data:", error);
        }
    }, [id, FisherID, searchTerm]);

    // Handle window resize for responsive design
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.addEventListener('resize', handleResize);
    }, []);

    // Fetch data and check expiry
    useEffect(() => {
        checkTokenValidity();
        CheckExpiry();
        FetchSamples();
    }, [FetchSamples, CheckExpiry]);

    // Debounce search implementation
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (searchTerm !== undefined) {
                FetchSamples();
            }
        }, 500); // 500ms debounce delay

        return () => clearTimeout(debounceTimer);
    }, [searchTerm, FetchSamples]);

    const indexOfLastEntry = currentPage * entriesPerPage;
    const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
    const currentEntries = samples.slice(indexOfFirstEntry, indexOfLastEntry);
    const totalPages = Math.ceil(samples.length / entriesPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Image Handling
    const openModal2 = (id) => {
        setSelectedID(id);
        setIsModalOpen(true);
        setSelectedImage(null);
    };

    const closeModal2 = () => {
        setIsModalOpen(false);
        setPreview(null);
        setSelectedImage(null);
    };

    const handleFileChange2 = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 4 * 1024 * 1024) {
                showErrorToast("Image size should be less than 4MB");
                return;
            }
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onload = () => setPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    // Add this function to handle manual file input trigger
    const triggerFileInput = () => {
        const fileInput = document.getElementById('file-input-webview');
        if (fileInput) {
            fileInput.click();
        }
    };

    const handleUpload = async () => {
        if (!selectedImage) {
            showErrorToast("Please select an image first");
            return;
        }

        setIsLoading(true);
        const formData = new FormData();
        formData.append('image', selectedImage);
        try {
            await axiosInstance.post(`/api/FishSampling/AddImageToSample/${selectedID}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showSuccessToast('Image uploaded successfully');
            FetchSamples();
        } catch (error) {
            handleApiError(error, "Failed to upload image");
        } finally {
            closeModal2();
            setIsLoading(false);
        }
    };

    const showImage = (blobData) => {
        if (blobData == null) {
            showErrorToast("No image to display");
            return;
        }
        try {
            const base64Image = imageFieldToDataUrl(blobData);
            if (!base64Image) {
                showErrorToast("Could not display image");
                return;
            }
            setImageToShow(base64Image);
            setModalVisible(true);
        } catch (err) {
            console.error(err);
            showErrorToast("Could not display image");
        }
    };

    // Update Handling
    const openUpdateModal = (sample) => {
        const selectedFish = availableFishes.find(fish => fish.FishTypeID === sample.FishTypeID);



        let equation = pond.LengthUnitName === "mm"
            ? selectedFish?.WsMGEquation || ""
            : selectedFish?.WsIPEquation || "";




        // Validate and normalize equation
        if (equation && !/^[0-9+\-*/(). L^log]*$/.test(equation)) {

            equation = "";
        }

        setSelectedSample({
            ...sample,
            equation,
            length: sample.Length.toString(),
            weight: sample.Weight.toString(),
        });

        // Initialize updateForm with current values
        setUpdateForm({
            length: sample.Length.toString() || "",
            weight: sample.Weight.toString() || "",
            ws: sample.Ws?.toString() || "",
            wr: sample.Wr?.toString() || "",
            tagNumber: sample.TagNumber || "",
            CaptureMethod: sample.CaptureMethod || sample.captureMethod || sessionCaptureMethod,
            EffortUnitID: sample.EffortUnitID || "",
            AnglerStaffID: sample.AnglerStaffID || "",
            Status: sample.Status || "Culled",
        });

        setIsUpdateModalOpen(true);
    };

    const closeUpdateModal = () => {
        setIsUpdateModalOpen(false);
        setSelectedSample(null);
        setUpdateForm({
            length: "", weight: "", ws: "", wr: "", tagNumber: "",
            CaptureMethod: "Angling", EffortUnitID: "", AnglerStaffID: "", Status: "",
        });
    };

    const handleUpdateInputChange = (field, value) => {
        let newValue = value;

        // For length and weight, store raw string to allow partial decimal inputs
        if (field === "length" || field === "weight") {
            if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                newValue = value;
            } else {
                return; // Ignore invalid input
            }
        } else if (field === "Status") {
            newValue = value;
        }

        // Update the form state
        const updatedForm = { ...updateForm, [field]: newValue };

        // Recalculate ws and wr when length or weight changes
        if (field === "length" || field === "weight") {
            const updatedSample = calculateWsWr({
                ...selectedSample,
                length: updatedForm.length, // Use string value from form
                weight: updatedForm.weight,
                equation: selectedSample.equation || "",
            });
            updatedForm.ws = updatedSample.ws?.toString() || "";
            updatedForm.wr = updatedSample.wr?.toString() || "";
        }

        setUpdateForm(updatedForm);
    };

    const handleUpdateSample = async () => {
        const parsedLength = parseFloat(updateForm.length);
        const parsedWeight = parseFloat(updateForm.weight);

        if (isNaN(parsedLength) || parsedLength <= 0 || isNaN(parsedWeight) || parsedWeight <= 0) {
            showErrorToast("Length and Weight must be valid positive numbers");
            return;
        }

        if (assignmentMode === "effort_unit") {
            if (!effortUnits.length) {
                showErrorToast("Configure areas, nets, or hauls on the General tab first");
                return;
            }
            if (!updateForm.EffortUnitID) {
                showErrorToast(`Please select a ${assignmentLabel.toLowerCase()}`);
                return;
            }
        }
        if (assignmentMode === "angler" && !updateForm.AnglerStaffID) {
            showErrorToast("Please select an angler");
            return;
        }

        // Recalculate ws and wr before submitting to ensure accuracy
        const updatedSample = calculateWsWr({
            ...selectedSample,
            length: parsedLength,
            weight: parsedWeight,
            equation: selectedSample.equation,
        });

        setIsLoading(true);
        try {
            await axiosInstance.put(`/api/FishSampling/UpdateSample`, {
                FishSamplingID: selectedSample.FishSamplingID,
                Length: parsedLength.toFixed(4),
                Weight: parsedWeight.toFixed(4),
                Ws: parseFloat(updatedSample.ws || 0).toFixed(4),
                Wr: parseFloat(updatedSample.wr || 0).toFixed(4),
                TagNumber: updateForm.tagNumber || '',
                CaptureMethod:
                    assignmentMode === "capture_method"
                        ? updateForm.CaptureMethod
                        : sessionCaptureMethod,
                ...(assignmentMode === "effort_unit"
                    ? { EffortUnitID: updateForm.EffortUnitID }
                    : {}),
                ...(assignmentMode === "angler"
                    ? { AnglerStaffID: updateForm.AnglerStaffID }
                    : {}),
                Status: updateForm.Status,
                PondDID: parseInt(id, 10),
                FishTypeID: parseInt(selectedSample.FishTypeID, 10),
                FisherID: parseInt(resolveScopedFisherID(userDetails, pond) || pond?.FisherID, 10),
            });
            showSuccessToast("Sample updated successfully");
            FetchSamples();
            closeUpdateModal();
        } catch (error) {
            handleApiError(error, "Failed to update sample");
        } finally {
            setIsLoading(false);
        }
    };

    // Delete Handling
    const handleDeleteSample = async (FishSamplingID) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axiosInstance.delete(`/api/FishSampling/DeleteSample/${FishSamplingID}`);
                showSuccessToast("Sample deleted successfully");
                FetchSamples();
            } catch (error) {
                handleApiError(error, "Failed to delete sample");
            } finally {
                setIsLoading(false);
            }
        }
    };

    const calculateWsWr = (sample) => {
        const length = parseFloat(sample.length);
        const weight = parseFloat(sample.weight);

        if (!sample.equation || isNaN(length) || length <= 0) {
            return { ...sample, ws: "", wr: "" };
        }

        try {
            const replacedExpression = sample.equation.replace(/L/g, length).replace(/\^/g, '**');

            if (!/^[0-9+\-*/(). L^log]*$/.test(sample.equation)) {
                console.error("Invalid equation format:", sample.equation);
                return { ...sample, ws: "", wr: "" };
            }

            const calculatedWs = Function(`return ${replacedExpression}`)();

            if (isNaN(calculatedWs) || !isFinite(calculatedWs)) {
                return { ...sample, ws: "", wr: "" };
            }

            if (calculatedWs > 0) {
                const ws = parseFloat(calculatedWs.toFixed(3));
                const wr = !isNaN(weight) && weight > 0
                    ? parseFloat((weight / calculatedWs).toFixed(3))
                    : "";

                return { ...sample, ws, wr };
            } else {
                return { ...sample, ws: "", wr: "" };
            }
        } catch (error) {
            console.error("Calculation error:", error, {
                equation: sample.equation,
                length: sample.length,
                weight: sample.weight
            });
            return { ...sample, ws: "", wr: "" };
        }
    };

    const getTagNumberFromRow = (row) =>
        row.TagNumber ||
        row["Tag Number"] ||
        row.tagNumber ||
        row.tagnumber ||
        "";

    const buildImportPreviewRow = (row) => {
        const fishType = availableFishes.find(
            (f) => f.FishTypeName.toLowerCase() === row.FishTypeName?.toLowerCase()
        );
        if (!fishType) return row;

        const calculatedValues = calculateWsWr({
            length: row.Length?.toString(),
            weight: row.Weight?.toString(),
            equation:
                pond.LengthUnitName === "mm" ? fishType.WsMGEquation : fishType.WsIPEquation,
        });

        const resolvedCm = resolveCaptureMethodForRow(row, pond);
        const effortResolved = resolveEffortUnitForRow(row, effortUnits);
        const anglerResolved = resolveAnglerForRow(row, pondAnglers);
        const effortLabel = effortResolved.value
            ? effortUnits.find((unit) => String(unit.id) === String(effortResolved.value))?.Label
            : null;
        const anglerLabel = anglerResolved.value
            ? pondAnglers.find((a) => String(a.staffId) === String(anglerResolved.value))?.fullName
            : null;
        let resolvedAssignment = "—";
        if (assignmentMode === "effort_unit") {
            resolvedAssignment = effortResolved.error
                ? `Invalid (${effortResolved.error})`
                : effortLabel || (getEffortUnitFromRow(row)
                    ? `Invalid (${getEffortUnitFromRow(row)} — not found in session effort units)`
                    : "—");
        } else if (assignmentMode === "angler") {
            resolvedAssignment = anglerResolved.error
                ? `Invalid (${anglerResolved.error})`
                : anglerLabel || "—";
        } else {
            resolvedAssignment = resolvedCm.error
                ? `Invalid (${resolvedCm.error})`
                : resolvedCm.value;
        }
        return {
            ...row,
            FishTypeName: fishType.FishTypeName,
            Ws: calculatedValues.ws || "",
            Wr: calculatedValues.wr || "",
            TagNumber: getTagNumberFromRow(row),
            _resolvedAssignment: resolvedAssignment,
        };
    };

    const buildSampleFromImportRow = (row, pondContext, effortUnitsForImport, importFisherID) => {
        const fishType = availableFishes.find(
            (f) => f.FishTypeName.toLowerCase() === row.FishTypeName.toLowerCase()
        );
        if (!fishType) {
            return { error: "Fish type not found", row, skipped: true };
        }

        const calculatedValues = calculateWsWr({
            length: row.Length.toString(),
            weight: row.Weight.toString(),
            equation:
                pondContext.LengthUnitName === "mm"
                    ? fishType.WsMGEquation
                    : fishType.WsIPEquation,
        });

        const captureResolved = resolveCaptureMethodForRow(row, pondContext);
        if (assignmentMode === "capture_method" && captureResolved.error) {
            return { error: captureResolved.error, row };
        }

        const effortResolved =
            assignmentMode === "effort_unit"
                ? resolveEffortUnitForRow(row, effortUnitsForImport)
                : { value: null };
        if (effortResolved.error) {
            return { error: effortResolved.error, row };
        }

        const anglerResolved =
            assignmentMode === "angler"
                ? resolveAnglerForRow(row, pondAnglers)
                : { value: null };
        if (anglerResolved.error) {
            return { error: anglerResolved.error, row };
        }

        return {
            sample: buildFishSamplePayload({
                PondDID: parseInt(id, 10),
                FisherID: importFisherID ?? pondContext?.FisherID,
                FishTypeID: fishType.FishTypeID,
                Length: parseFloat(row.Length).toFixed(4),
                Weight: parseFloat(row.Weight).toFixed(4),
                Ws: parseFloat(calculatedValues.ws || 0).toFixed(4),
                Wr: parseFloat(calculatedValues.wr || 0).toFixed(4),
                TagNumber: getTagNumberFromRow(row),
                Status: row.Status || "",
                CaptureMethod:
                    assignmentMode === "capture_method"
                        ? captureResolved.value
                        : sessionCaptureMethod,
                ...(assignmentMode === "effort_unit"
                    ? { EffortUnitID: effortResolved.value }
                    : {}),
                ...(assignmentMode === "angler"
                    ? { AnglerStaffID: anglerResolved.value }
                    : {}),
            }),
            row,
        };
    };

    const handleApiError = (error, defaultMessage) => {
        if (error.response?.status === 401) {
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            window.location.reload();
            showErrorToast("Session expired. Please log in again.");
        } else {
            const errorMessage = error.response?.data?.message || defaultMessage;
            showErrorToast(errorMessage);
            console.error(errorMessage, error);
        }
    };

    // Analysis Modal Handlers
    const openAnalysisModal = () => {
        setIsAnalysisModalOpen(true);
    };

    const closeAnalysisModal = () => {
        setIsAnalysisModalOpen(false);
    };

    const handleTableClick = () => {
        closeAnalysisModal();
        history.push(`/FishDataTableView/${id}`);
    };

    const handleChartClick = () => {
        closeAnalysisModal();
        history.push(`/fish-data-graph-new/${id}`);
    };

    const generateExcelTemplate = () => {
        const mode = getFishAssignmentMode(pond?.SamplingMethod);
        const columnSpec = getExcelImportColumnSpec(mode);

        if (mode === "effort_unit" && effortUnits.length === 0) {
            showErrorToast(
                "Configure areas, nets, or hauls on the General tab before downloading the import template."
            );
            return;
        }
        if (mode === "angler" && pondAnglers.length === 0) {
            showErrorToast(
                "No anglers selected for this session. Choose anglers on the General tab before downloading the template."
            );
            return;
        }
        if (!availableFishes.length) {
            showErrorToast(
                "Add fish types to this sampling session before downloading the import template."
            );
            return;
        }

        const wb = XLSX.utils.book_new();
        const headers = columnSpec.map((col) => col.header);

        const sortedUnits = [...effortUnits].sort(
            (a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0)
        );
        const exampleAssignment =
            mode === "effort_unit"
                ? sortedUnits[0]?.Label || "Area 1"
                : mode === "angler"
                    ? pondAnglers[0]?.fullName || ""
                    : mapSamplingMethodToCaptureMethod(pond?.SamplingMethod) || "Angling";

        const exampleRow = columnSpec.map((col) => {
            if (col.key === "FishTypeName") return EXCEL_EXAMPLE_ROW_MARKER;
            if (col.key === "Length") return "";
            if (col.key === "Weight") return "";
            if (col.key === "TagNumber") return "";
            if (col.key === "EffortUnit") return exampleAssignment;
            if (col.key === "Angler") return exampleAssignment;
            if (col.key === "CaptureMethod") return "";
            if (col.key === "Status") return "Culled";
            return "";
        });

        const samplingSheetRows = [headers, exampleRow];
        const wsData = XLSX.utils.aoa_to_sheet(samplingSheetRows);
        wsData["!cols"] = columnSpec.map((col) => ({
            wch: col.key === "FishTypeName" ? 34 : col.required ? 16 : 14,
        }));

        const assignmentLabel = getFishAssignmentColumnLabel(pond?.SamplingMethod);
        const instructionRows = [
            ["Fish Sampling Import Template"],
            [],
            ["Session", normalizeSamplingMethod(pond?.SamplingMethod) || "—"],
            ["Pond detail", pond?.Name || id],
            ["Length unit", lengthUnit || "—"],
            ["Weight unit", weightUnit || "—"],
            [],
            ["Required columns on the Sampling Data sheet:"],
            ["Column", "Required?", "Description"],
            ...columnSpec.map((col) => [
                col.header,
                col.required ? "Yes" : "No",
                col.description,
            ]),
            [],
            ["How to use:"],
            ["1. Open the Sampling Data sheet."],
            ["2. Row 1 = column names. Row 2 = example (delete or replace with your fish)."],
            ["3. Enter one row per fish. Copy fish names from Fish Types List."],
            mode === "effort_unit"
                ? [`4. Fill the EffortUnit column with labels from the Effort Units sheet (${assignmentLabel}).`]
                : mode === "angler"
                    ? ["4. Fill the Angler column with names from the Anglers sheet."]
                    : ["4. CaptureMethod is optional when the session method is fixed on General."],
            ["5. Save and upload the file on the Fish Samples page."],
            [],
            ["Note: CaptureMethod is not in this template when the session uses a single sampling method."],
        ];
        const wsInstructions = XLSX.utils.aoa_to_sheet(instructionRows);
        wsInstructions["!cols"] = [{ wch: 18 }, { wch: 12 }, { wch: 70 }];

        const fishTypesData = [
            ["Available Fish Types (copy into FishTypeName):"],
            [],
            ...availableFishes.map((fish) => [fish.FishTypeName]),
        ];
        const wsFishTypes = XLSX.utils.aoa_to_sheet(fishTypesData);
        wsFishTypes["!cols"] = [{ wch: 60 }];

        const statusData = [
            ["Status Options (optional column):"],
            [],
            ["Culled"],
            ["Released"],
        ];
        const wsStatus = XLSX.utils.aoa_to_sheet(statusData);
        wsStatus["!cols"] = [{ wch: 40 }];

        const captureMethodData = [
            ["Capture Method Options:"],
            ["Only for sessions that allow mixed capture methods."],
            [],
            ...CAPTURE_METHOD_OPTIONS.map((m) => [m]),
        ];
        const wsCapture = XLSX.utils.aoa_to_sheet(captureMethodData);
        wsCapture["!cols"] = [{ wch: 55 }];

        if (mode === "effort_unit" && effortUnits.length > 0) {
            const effortRows = [
                ["Effort Units for this session (use in EffortUnit column):"],
                [],
                ["Label"],
                ...sortedUnits.map((unit) => [unit.Label]),
                [],
                ["You may also use the unit number only (1, 2, 3, ...)."],
            ];
            const wsEffort = XLSX.utils.aoa_to_sheet(effortRows);
            wsEffort["!cols"] = [{ wch: 30 }];
            XLSX.utils.book_append_sheet(wb, wsEffort, "Effort Units");
        }

        if (mode === "angler" && pondAnglers.length > 0) {
            const anglerRows = [
                ["Anglers for this pond (use in Angler column):"],
                [],
                ["Name"],
                ...pondAnglers.map((a) => [a.fullName]),
            ];
            const wsAnglers = XLSX.utils.aoa_to_sheet(anglerRows);
            wsAnglers["!cols"] = [{ wch: 40 }];
            XLSX.utils.book_append_sheet(wb, wsAnglers, "Anglers");
        }

        XLSX.utils.book_append_sheet(wb, wsInstructions, "Instructions");
        XLSX.utils.book_append_sheet(wb, wsData, "Sampling Data");
        XLSX.utils.book_append_sheet(wb, wsFishTypes, "Fish Types List");
        if (mode === "capture_method") {
            XLSX.utils.book_append_sheet(wb, wsCapture, "Capture Method Options");
        }
        XLSX.utils.book_append_sheet(wb, wsStatus, "Status Options");

        const methodSlug = String(normalizeSamplingMethod(pond?.SamplingMethod) || "sampling")
            .replace(/\s+/g, "_")
            .toLowerCase();
        XLSX.writeFile(wb, `fish_sampling_${methodSlug}_${id}.xlsx`);
    };

    const handleImportModalOpen = () => {
        setIsImportModalOpen(true);
        setImportFile(null);
        setImportErrors([]);
        setPreviewData([]);
        setPreviewTotalRows(0);
        setShowPreview(false);
        setIsParsingFile(false);
    };

    const handleImportModalClose = () => {
        setIsImportModalOpen(false);
        setImportFile(null);
        setImportErrors([]);
        setPreviewData([]);
        setPreviewTotalRows(0);
        setShowPreview(false);
        setIsParsingFile(false);
        setImportProgress({ current: 0, total: 0, currentRow: null });
        setProcessedRows([]);
    };

    const handleImportFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.match(/\.(xlsx|xls)$/)) {
            showErrorToast("Please upload an Excel file");
            return;
        }

        setImportFile(file);
        setImportErrors([]);
        setPreviewData([]);
        setPreviewTotalRows(0);
        setShowPreview(false);
        setIsParsingFile(true);

        const reader = new FileReader();
        reader.onload = (e) => {
            setTimeout(() => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const worksheet = getSamplingDataWorksheet(workbook);
                    if (!worksheet) {
                        showErrorToast("Could not find a data sheet in the workbook.");
                        return;
                    }
                    const jsonData = filterImportRows(XLSX.utils.sheet_to_json(worksheet));
                    const previewWithCalculations = jsonData
                        .slice(0, IMPORT_PREVIEW_ROW_LIMIT)
                        .map(buildImportPreviewRow);

                    setPreviewData(previewWithCalculations);
                    setPreviewTotalRows(jsonData.length);
                    setShowPreview(jsonData.length > 0);
                } catch (error) {
                    console.error("Error reading file:", error);
                    showErrorToast("Error reading file. Please check the format.");
                } finally {
                    setIsParsingFile(false);
                }
            }, 0);
        };
        reader.onerror = () => {
            setIsParsingFile(false);
            showErrorToast("Error reading file. Please try again.");
        };
        reader.readAsArrayBuffer(file);
    };

    const validateImportData = (data, unitsForValidation = effortUnits) => {
        const errors = [];
        const mode = getFishAssignmentMode(pond?.SamplingMethod);
        const importRows = filterImportRows(data);

        if (mode === "effort_unit" && unitsForValidation.length === 0) {
            errors.push(
                "This sampling method requires effort units. Save the session on the General tab with the number of areas/nets/hauls before importing fish."
            );
            return errors;
        }

        if (mode === "angler" && pondAnglers.length === 0) {
            errors.push(
                "No anglers selected for this session. Choose anglers on the General tab before importing fish."
            );
            return errors;
        }

        const columnCheck = validateExcelRequiredColumns(data, mode);
        if (!columnCheck.ok) {
            errors.push(columnCheck.message);
            return errors;
        }

        importRows.forEach((row, index) => {
            if (errors.length >= 25) return;
            if (!row.FishTypeName || !availableFishes.find(f => f.FishTypeName.toLowerCase() === row.FishTypeName.toLowerCase())) {
                errors.push(`Row ${index + 1}: Invalid fish type`);
            }
            if (!row.Length || isNaN(row.Length) || row.Length <= 0) {
                errors.push(`Row ${index + 1}: Invalid length`);
            }
            if (!row.Weight || isNaN(row.Weight) || row.Weight <= 0) {
                errors.push(`Row ${index + 1}: Invalid weight`);
            }
            if (row.Status && !['Culled', 'Released'].includes(row.Status)) {
                errors.push(`Row ${index + 1}: Status must be either 'Culled' or 'Released' (or leave empty)`);
            }
            if (mode === "capture_method" || getRawCaptureMethodFromRow(row)) {
                const rawCm = getRawCaptureMethodFromRow(row);
                const cmNorm = normalizeCaptureMethodToken(rawCm);
                if (!cmNorm.ok) {
                    errors.push(
                        `Row ${index + 1}: ${cmNorm.message}. Use a value from the Capture Method Options sheet.`
                    );
                }
            }
            if (mode === "effort_unit") {
                const effortResolved = resolveEffortUnitForRow(row, unitsForValidation);
                if (effortResolved.error) {
                    errors.push(`Row ${index + 1}: ${effortResolved.error}`);
                } else if (effortResolved.value == null) {
                    errors.push(`Row ${index + 1}: EffortUnit is required for ${pond?.SamplingMethod} sessions`);
                }
            }
            if (mode === "angler") {
                const anglerResolved = resolveAnglerForRow(row, pondAnglers);
                if (anglerResolved.error) {
                    errors.push(`Row ${index + 1}: ${anglerResolved.error}`);
                } else if (anglerResolved.value == null) {
                    errors.push(`Row ${index + 1}: Angler is required for Fishing sessions`);
                }
            }
        });
        if (importRows.length > 0 && errors.length >= 25) {
            errors.push(`Additional validation errors were omitted (showing first 25).`);
        }
        return errors;
    };

    const handleImportSubmit = async () => {
        if (!importFile) {
            showErrorToast("Please select a file to import");
            return;
        }

        setIsImporting(true);
        setImportProgress({ current: 0, total: 0, currentRow: null });
        setProcessedRows([]);

        try {
            const fileData = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = (e) => reject(e);
                reader.readAsArrayBuffer(importFile);
            });

            const data = new Uint8Array(fileData);
            const workbook = XLSX.read(data, { type: "array" });
            const worksheet = getSamplingDataWorksheet(workbook);
            if (!worksheet) {
                showErrorToast("Could not find a data sheet in the workbook.");
                setIsImporting(false);
                return;
            }
            const jsonData = filterImportRows(XLSX.utils.sheet_to_json(worksheet));

            const detailParams = { id };
            const scopedFisher = resolveScopedFisherID(userDetails, pond);
            if (scopedFisher != null && scopedFisher !== "") {
                detailParams.FisherID = scopedFisher;
            }
            const pondRefresh = await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
                params: detailParams,
            });
            const freshPond = pondRefresh.data || pond;
            const freshEffortUnits = freshPond.EffortUnits || [];

            const validationErrors = validateImportData(jsonData, freshEffortUnits);
            if (validationErrors.length > 0) {
                setImportErrors(validationErrors);
                setIsImporting(false);
                setTimeout(() => {
                    const errorElement = document.querySelector(".bg-danger");
                    if (errorElement) {
                        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                }, 100);
                return;
            }

            setImportProgress({ current: 0, total: jsonData.length, currentRow: null });

            const importFisherID = resolveScopedFisherID(userDetails, freshPond);
            const samplesToImport = [];
            const rowResults = [];

            for (let index = 0; index < jsonData.length; index++) {
                const row = jsonData[index];
                const built = buildSampleFromImportRow(
                    row,
                    freshPond,
                    freshEffortUnits,
                    importFisherID
                );

                if (built.error) {
                    rowResults.push({
                        index: index + 1,
                        status: built.skipped ? "skipped" : "error",
                        data: row,
                        error: built.skipped ? undefined : built.error,
                        reason: built.skipped ? built.error : undefined,
                    });
                } else {
                    samplesToImport.push(built.sample);
                    rowResults.push({
                        index: index + 1,
                        status: "success",
                        data: row,
                    });
                }

                if (
                    index === 0 ||
                    index === jsonData.length - 1 ||
                    (index + 1) % IMPORT_PROGRESS_YIELD_EVERY === 0
                ) {
                    setImportProgress({
                        current: index + 1,
                        total: jsonData.length,
                        currentRow: { ...row, index: index + 1 },
                    });
                    await yieldToUi();
                }
            }

            if (samplesToImport.length === 0) {
                setProcessedRows(rowResults.slice(-10));
                showErrorToast("No valid rows to import");
                setIsImporting(false);
                return;
            }

            setProcessedRows(rowResults.slice(-10));
            setImportProgress({
                current: samplesToImport.length,
                total: samplesToImport.length,
                currentRow: null,
            });

            const notifyEmail = userDetails?.Email || userDetails?.email || null;
            const notifyName = userDetails?.FullName || userDetails?.Name || null;
            const queueRes = await axiosInstance.post(
                "/api/FishSampling/AddSamplesAsync",
                {
                    samples: samplesToImport,
                    FileName: importFile.name,
                    NotifyEmail: notifyEmail,
                    NotifyName: notifyName,
                },
                { timeout: 120000 }
            );

            const queuedMessage =
                queueRes.data?.message ||
                `Import queued for ${samplesToImport.length} sample${samplesToImport.length === 1 ? "" : "s"}. We will email you when it finishes.`;
            showSuccessToast(queuedMessage);
            handleImportModalClose();
            // Samples appear after the background job finishes; refresh may still be empty.
            FetchSamples();
        } catch (error) {
            handleApiError(error, "Failed to process import file");
            setTimeout(() => {
                const errorElement = document.querySelector(".bg-danger");
                if (errorElement) {
                    errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 100);
        } finally {
            setIsImporting(false);
        }
    };

    return (
        isLoading ? <Preloader /> :
            <div className="donezo-page donezo-workflow-page py-3">
            <div className="container-fluid px-3 px-md-4">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
                    <div className="mb-3 mb-md-0 flex-grow-1" style={{ minWidth: 0 }}>
                        <PondDetailWorkflowPageHeader
                            pondDetailId={id}
                            section="sampling"
                            detailName={pond?.Name}
                            noBottomMargin
                        />
                    </div>
                    <div className="ms-md-auto">
                        {canEditSampling && (
                            <div>
                                <Link
                                    to={"/AddSamplingDetail/" + id}
                                    className="btn btn-success btn-rounded px-3 px-md-4 rounded-pill m-2"
                                >
                                    <i className="fa fa-plus me-1"></i>
                                    {windowWidth > 576 ? "Add fish" : "Add fish"}
                                </Link>
                                <button
                                    onClick={openAnalysisModal}
                                    className="btn btn-success btn-rounded px-3 px-md-4 rounded-pill me-2"
                                >
                                    <i className="fa fa-chart-line me-1"></i>
                                    {windowWidth > 576 ? "Data Analysis" : "Analyze"}
                                </button>
                                <button
                                    onClick={handleImportModalOpen}
                                    className="btn btn-primary btn-rounded px-3 px-md-4 rounded-pill"
                                >
                                    <i className="fa fa-file-excel me-1"></i>
                                    {windowWidth > 576 ? "Import Excel" : "Import"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <PondDetailWorkflowTabs id={id} active="sampling" />

                {/* Tab Content */}
                <div className="card border-0 shadow-sm donezo-workflow-card">
                    <div className="tab-content p-3 p-md-4">
                        <div className="input-group mb-3">
                            <div className="flex-fill position-relative">
                                <div className="input-group">
                                    <div
                                        className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                                        style={{ zIndex: 10 }}
                                    >
                                        <i className="fa fa-search opacity-5"></i>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control px-35px bg-light"
                                        placeholder="Search Sample..."
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Desktop Table */}
                        <div className="d-none d-md-block">
                            <div className="table-responsive">
                                <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th>Fish Type</th>
                                            <th>Length ({lengthUnit || "MM"})</th>
                                            <th>Weight ({weightUnit || "g"})</th>
                                            <th>
                                              <WsLabel />
                                            </th>
                                            <th>
                                              <WrLabel /> (%)
                                            </th>
                                            <th>Tag Number</th>
                                            <th>Capture Method</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentEntries.map((web) => (
                                            <tr
                                                key={web.FishSamplingID}
                                                onClick={() => openUpdateModal(web)}
                                                style={{ cursor: canEditSampling ? 'pointer' : 'default' }}
                                            >
                                                <td>{web.FishTypeName}</td>
                                                <td>{web.Length}</td>
                                                <td>{web.Weight}</td>
                                                <td>{web.Ws}</td>
                                                <td>{formatWrAsPercent(web.Wr)}</td>
                                                <td>{web.TagNumber || '-'}</td>
                                                <td>{web.CaptureMethod || web.captureMethod || '-'}</td>
                                                <td>
                                                    {web.Status === "Culled" ? (
                                                        <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                                            <i className="fa fa-circle fs-9px fa-fw me-5px"></i>
                                                            Culled
                                                        </span>
                                                    ) : web.Status === "Released" ? (
                                                        <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                                            <i className="fa fa-circle fs-9px fa-fw me-5px"></i>
                                                            Released
                                                        </span>
                                                    ) : (
                                                        <span className="badge border border-primary text-primary px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                                            <i className="fa fa-circle fs-9px fa-fw me-5px"></i>
                                                            Not Set
                                                        </span>
                                                    )}
                                                </td>
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    {web.Image && (
                                                        <span
                                                            onClick={() => showImage(web.Image)}
                                                            style={{ margin: "0 5px", cursor: "pointer" }}
                                                            title="View Image"
                                                        >
                                                            <i className="fa fa-eye fa-lg text-primary-900"></i>
                                                        </span>
                                                    )}
                                                    <span
                                                        onClick={() => openModal2(web.FishSamplingID)}
                                                        style={{ margin: "0 5px", cursor: "pointer" }}
                                                        title="Upload Image"
                                                    >
                                                        <i className="fa fa-upload fa-lg text-secondary-900"></i>
                                                    </span>
                                                    {canEditSampling && (
                                                        <>
                                                            <span
                                                                onClick={() => openUpdateModal(web)}
                                                                style={{ margin: "0 5px", cursor: "pointer" }}
                                                                title="Edit Sample"
                                                            >
                                                                <i className="fa fa-edit fa-lg text-warning-900"></i>
                                                            </span>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleDeleteSample(web.FishSamplingID)}
                                                                title="Delete Sample"
                                                            >
                                                                <i className="fa fa-trash"></i>
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Mobile Cards */}
                        <div className="d-md-none">
                            {currentEntries.map((web) => (
                                <div key={web.FishSamplingID} className="card mb-3 shadow-sm">
                                    <div
                                        className="card-body p-3"
                                        onClick={() => canEditSampling && openUpdateModal(web)}
                                        style={{ cursor: canEditSampling ? 'pointer' : 'default' }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="card-title mb-0">{web.FishTypeName}</h5>
                                            {canEditSampling && (
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteSample(web.FishSamplingID);
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                    title="Delete Sample"
                                                >
                                                    <i className="fa fa-trash fa-lg text-danger-900"></i>
                                                </span>
                                            )}
                                        </div>
                                        <div className="row mt-2">
                                            <div className="col-6">
                                                <p className="mb-1">
                                                    <small className="text-muted">Length:</small> {web.Length} {lengthUnit || "MM"}
                                                </p>
                                            </div>
                                            <div className="col-6">
                                                <p className="mb-1">
                                                    <small className="text-muted">Weight:</small> {web.Weight} {weightUnit || "g"}
                                                </p>
                                            </div>
                                            <div className="col-6">
                                                <p className="mb-1">
                                                    <small className="text-muted">Ws:</small> {web.Ws}
                                                </p>
                                            </div>
                                            <div className="col-6">
                                                <p className="mb-1">
                                                    <small className="text-muted">Wr:</small> {formatWrAsPercent(web.Wr)}
                                                </p>
                                            </div>
                                            <div className="col-6">
                                                <p className="mb-1">
                                                    <small className="text-muted">Tag Number:</small> {web.TagNumber || '-'}
                                                </p>
                                            </div>
                                            <div className="col-6">
                                                <p className="mb-1">
                                                    <small className="text-muted">Capture Method:</small> {web.CaptureMethod || web.captureMethod || '-'}
                                                </p>
                                            </div>
                                            <div className="col-6">
                                                <p className="mb-1">
                                                    <small className="text-muted">Status:</small>
                                                    {web.Status === "Culled" ? (
                                                        <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                                            <i className="fa fa-circle fs-9px fa-fw me-5px"></i>
                                                            Culled
                                                        </span>
                                                    ) : (
                                                        <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                                            <i className="fa fa-circle fs-9px fa-fw me-5px"></i>
                                                            Released
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            <div className="col-12 mt-2" onClick={(e) => e.stopPropagation()}>
                                                <p className="mb-1">
                                                    <small className="text-muted">Actions:</small>
                                                    {web.Image && (
                                                        <span
                                                            onClick={() => showImage(web.Image)}
                                                            style={{ margin: "0 10px", cursor: "pointer" }}
                                                            title="View Image"
                                                        >
                                                            <i className="fa fa-eye fa-lg text-primary-900"></i>
                                                        </span>
                                                    )}
                                                    {canEditSampling && (
                                                        <>
                                                            <span
                                                                onClick={() => openModal2(web.FishSamplingID)}
                                                                style={{ margin: "0 10px", cursor: "pointer" }}
                                                                title="Upload Image"
                                                            >
                                                                <i className="fa fa-upload fa-lg text-secondary-900"></i>
                                                            </span>
                                                            <span
                                                                onClick={() => openUpdateModal(web)}
                                                                style={{ margin: "0 10px", cursor: "pointer" }}
                                                                title="Edit Sample"
                                                            >
                                                                <i className="fa fa-edit fa-lg text-warning-900"></i>
                                                            </span>
                                                        </>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        <div className="d-md-flex align-items-center mt-3">
                            <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
                                Showing {samples.length === 0 ? indexOfFirstEntry : indexOfFirstEntry + 1} to{" "}
                                {indexOfLastEntry > samples.length ? samples.length : indexOfLastEntry} of {samples.length} entries
                            </div>
                            <ul className="pagination mb-0 justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    >
                                        {windowWidth < 768 ? '«' : 'Previous'}
                                    </button>
                                </li>
                                {windowWidth < 768 ? (
                                    <li className="page-item active">
                                        <span className="page-link">
                                            {currentPage} / {totalPages}
                                        </span>
                                    </li>
                                ) : (
                                    [...Array(totalPages)].map((_, index) => (
                                        <li
                                            key={index}
                                            className={`page-item ${index + 1 === currentPage ? "active" : ""}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(index + 1)}
                                            >
                                                {index + 1}
                                            </button>
                                        </li>
                                    ))
                                )}
                                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    >
                                        {windowWidth < 768 ? '»' : 'Next'}
                                    </button>
                                </li>
                            </ul>
                        </div>

                        {/* Image Preview Modal */}
                        <CustomModal
                            isOpen={modalVisible}
                            onClose={() => setModalVisible(false)}
                            title="Fish Image"
                            showCancelButton={true}
                            showButton={true}
                            showSubmitButton={false}
                            modalSize="modal-lg"
                            minHeight="400px"
                        >
                            <div style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#fff",
                            }}>
                                <img
                                    src={imageToShow}
                                    alt="Fish"
                                    style={{
                                        maxWidth: "100%",
                                        objectFit: "contain",
                                    }}
                                />
                            </div>
                        </CustomModal>

                        {/* Image Upload Modal */}
                        <CustomModal
                            isOpen={isModalOpen}
                            onClose={closeModal2}
                            title="Upload Fish Image"
                            onSubmit={handleUpload}
                            showCancelButton={true}
                            showButton={true}
                            showSubmitButton={true}
                            modalSize="modal-lg"
                            minHeight="auto"
                        >
                            <span className="text-danger">(Size Should be Lower than 4MB)</span>
                            <div className="mt-3">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange2}
                                    className="form-control"
                                />
                                {preview && (
                                    <div className="row pt-3">
                                        <img
                                            src={preview}
                                            alt="Selected Preview"
                                            style={{
                                                width: "100%",
                                                objectFit: "contain",
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </CustomModal>

                        {/* Update Sample Modal */}
                        <CustomModal
                            isOpen={isUpdateModalOpen}
                            onClose={closeUpdateModal}
                            onSubmit={handleUpdateSample}
                            title="Update Fish Sample"
                            showButton={true}
                            showCancelButton={true}
                            showSubmitButton={true}
                            modalSize="modal-lg"
                            minHeight="auto"
                        >
                            {selectedSample && (
                                <div className="mt-3">
                                    <div className="mb-3">
                                        <div className="form-floating mb-15px">
                                            <input
                                                type="text"
                                                className="form-control h-45px fs-13px"
                                                placeholder="FishTypeName"
                                                id="FishTypeName"
                                                value={selectedSample.FishTypeName}
                                                readOnly
                                            />
                                            <label htmlFor="FishTypeName" className="d-flex align-items-center fs-13px text-gray-600">
                                                Fish Name
                                            </label>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6 mb-3">
                                            <div className="form-floating mb-15px">
                                                <input
                                                    type="text"
                                                    className="form-control h-45px fs-13px"
                                                    placeholder="Length"
                                                    id="length"
                                                    value={updateForm.length}
                                                    onChange={(e) => handleUpdateInputChange("length", e.target.value)}
                                                    required
                                                />
                                                <label htmlFor="length" className="d-flex align-items-center fs-13px text-gray-600">
                                                    Length ({lengthUnit || "MM"})
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-6 mb-3">
                                            <div className="form-floating mb-15px">
                                                <input
                                                    type="text"
                                                    className="form-control h-45px fs-13px"
                                                    placeholder="Weight"
                                                    id="Weight"
                                                    value={updateForm.weight}
                                                    onChange={(e) => handleUpdateInputChange("weight", e.target.value)}
                                                    required
                                                />
                                                <label htmlFor="Weight" className="d-flex align-items-center fs-13px text-gray-600">
                                                    Weight ({weightUnit || "g"})
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6 mb-3">
                                            <div className="form-floating mb-15px">
                                                <input
                                                    type="text"
                                                    className="form-control h-45px fs-13px"
                                                    placeholder="Ws"
                                                    id="ws"
                                                    value={updateForm.ws}
                                                    readOnly
                                                />
                                                <label htmlFor="ws" className="d-flex align-items-center fs-13px text-gray-600">
                                                    Ws
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-6 mb-3">
                                            <div className="form-floating mb-15px">
                                                <input
                                                    type="text"
                                                    className="form-control h-45px fs-13px"
                                                    placeholder="Wr"
                                                    id="wr"
                                                    value={formatWrAsPercent(updateForm.wr)}
                                                    readOnly
                                                />
                                                <label htmlFor="wr" className="d-flex align-items-center fs-13px text-gray-600">
                                                    Wr (%)
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="form-floating mb-15px">
                                            <input
                                                type="text"
                                                className="form-control h-45px fs-13px"
                                                placeholder="Tag Number"
                                                id="tagNumber"
                                                value={updateForm.tagNumber}
                                                onChange={(e) => handleUpdateInputChange("tagNumber", e.target.value)}
                                            />
                                            <label htmlFor="tagNumber" className="d-flex align-items-center fs-13px text-gray-600">
                                                Tag Number
                                            </label>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="form-floating mb-15px">
                                            {assignmentMode === "effort_unit" ? (
                                                <select
                                                    className="form-control"
                                                    id="assignmentField"
                                                    value={updateForm.EffortUnitID || ""}
                                                    onChange={(e) => handleUpdateInputChange("EffortUnitID", e.target.value)}
                                                >
                                                    <option value="">Select…</option>
                                                    {effortUnits.map((unit) => (
                                                        <option key={unit.id} value={unit.id}>
                                                            {unit.Label}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : assignmentMode === "angler" ? (
                                                <select
                                                    className="form-control"
                                                    id="assignmentField"
                                                    value={updateForm.AnglerStaffID || ""}
                                                    onChange={(e) => handleUpdateInputChange("AnglerStaffID", e.target.value)}
                                                >
                                                    <option value="">Select angler…</option>
                                                    {pondAnglers.map((angler) => (
                                                        <option key={angler.staffId} value={angler.staffId}>
                                                            {angler.fullName}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <select
                                                    className="form-control"
                                                    id="CaptureMethod"
                                                    value={updateForm.CaptureMethod || "Angling"}
                                                    onChange={(e) => handleUpdateInputChange("CaptureMethod", e.target.value)}
                                                >
                                                    {CAPTURE_METHOD_OPTIONS.map((method) => (
                                                        <option key={method} value={method}>
                                                            {displayMethodLabel(method)}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                            <label htmlFor="assignmentField" className="d-flex align-items-center fs-13px text-gray-600">
                                                {assignmentLabel}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="form-floating mb-15px">
                                            <select
                                                className="form-control"
                                                value={updateForm.Status}
                                                onChange={(e) => handleUpdateInputChange("Status", e.target.value)}
                                            >
                                                <option value="Culled">Culled</option>
                                                <option value="Released">Released</option>
                                            </select>
                                            <label htmlFor="Status" className="d-flex align-items-center fs-13px text-gray-600">
                                                Status
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CustomModal>

                        {/* Data Analysis Modal */}
                        <CustomModal
                            isOpen={isAnalysisModalOpen}
                            onClose={closeAnalysisModal}
                            showCancelButton={true}
                            title={
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-chart-network text-primary me-2" style={{ fontSize: '24px' }}></i>
                                    <span>Fish Sampling Analysis</span>
                                </div>
                            }
                            showButton={true}
                            showSubmitButton={false}
                            modalSize="modal-xl"
                            minHeight="auto"
                        >
                            <div className="mt-3">
                                <div className="row">
                                    {/* Table Analysis Card */}
                                    <div className="col-md-6 mb-3">
                                        <div className="card h-100 border-0 shadow-sm analysis-card"
                                            onClick={handleTableClick}
                                            style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                        >
                                            <div className="card-body text-center p-4">
                                                <div className="mb-3">
                                                    <i className="fas fa-table text-primary" style={{ fontSize: '48px' }}></i>
                                                </div>
                                                <h5 className="card-title mb-3">Comprehensive Table View</h5>
                                                <p className="card-text text-muted mb-4">
                                                    Detailed analysis with species summaries, measurements, and statistical data.
                                                </p>
                                                <div className="features-list text-start mb-4">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <i className="fas fa-fish text-primary me-2"></i>
                                                        <span>Species-wise Summary</span>
                                                    </div>
                                                    <div className="d-flex align-items-center mb-2">
                                                        <i className="fas fa-calculator text-primary me-2"></i>
                                                        <span>Length/Weight Analysis</span>
                                                    </div>
                                                    <div className="d-flex align-items-center mb-2">
                                                        <i className="fas fa-ruler-combined text-primary me-2"></i>
                                                        <span>Ws/Wr Calculations</span>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <i className="fas fa-file-export text-primary me-2"></i>
                                                        <span>Excel & PDF Export</span>
                                                    </div>
                                                </div>
                                                <div className="export-options bg-light rounded p-2 mb-3">
                                                    <small className="text-muted d-block mb-2">Export Features:</small>
                                                    <div className="d-flex justify-content-around">
                                                        <span><i className="fas fa-table text-success"></i> Merged View</span>
                                                        <span><i className="fas fa-calculator text-info"></i> Bin Analysis</span>
                                                        <span><i className="fas fa-chart-bar text-warning"></i> Statistics</span>
                                                    </div>
                                                </div>
                                                <button className="btn btn-primary btn-lg w-100 rounded-pill">
                                                    <i className="fas fa-table me-2"></i>
                                                    Open Table Analysis
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Graph Analysis Card */}
                                    <div className="col-md-6 mb-3">
                                        <div className="card h-100 border-0 shadow-sm analysis-card"
                                            onClick={handleChartClick}
                                            style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                        >
                                            <div className="card-body text-center p-4">
                                                <div className="mb-3">
                                                    <i className="fas fa-chart-line text-success" style={{ fontSize: '48px' }}></i>
                                                </div>
                                                <h5 className="card-title mb-3">Interactive Graph Analysis</h5>
                                                <p className="card-text text-muted mb-4">
                                                    Visual data representation with interactive charts and bin analysis.
                                                </p>
                                                <div className="features-list text-start mb-4">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <i className="fas fa-chart-scatter text-success me-2"></i>
                                                        <span>Length-Weight Relationship</span>
                                                    </div>
                                                    <div className="d-flex align-items-center mb-2">
                                                        <i className="fas fa-chart-bar text-success me-2"></i>
                                                        <span>Distribution Analysis</span>
                                                    </div>
                                                    <div className="d-flex align-items-center mb-2">
                                                        <i className="fas fa-chart-pie text-success me-2"></i>
                                                        <span>Species Composition</span>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <i className="fas fa-file-pdf text-success me-2"></i>
                                                        <span>PDF Export with Charts</span>
                                                    </div>
                                                </div>
                                                <div className="chart-types bg-light rounded p-2 mb-3">
                                                    <small className="text-muted d-block mb-2">Available Charts:</small>
                                                    <div className="d-flex justify-content-around">
                                                        <span><i className="fas fa-chart-line text-primary"></i> Length-Weight</span>
                                                        <span><i className="fas fa-chart-bar text-success"></i> Distribution</span>
                                                        <span><i className="fas fa-chart-pie text-warning"></i> Composition</span>
                                                    </div>
                                                </div>
                                                <button className="btn btn-success btn-lg w-100 rounded-pill">
                                                    <i className="fas fa-chart-line me-2"></i>
                                                    Open Graph Analysis
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Analysis Features Section */}
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-body">
                                                <h6 className="mb-3">
                                                    <i className="fas fa-tools text-info me-2"></i>
                                                    Advanced Analysis Tools
                                                </h6>
                                                <div className="row g-3">
                                                    <div className="col-md-3 col-6">
                                                        <div className="d-flex align-items-center">
                                                            <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-2">
                                                                <i className="fas fa-ruler text-primary"></i>
                                                            </div>
                                                            <span className="small">Length/Weight Bins</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 col-6">
                                                        <div className="d-flex align-items-center">
                                                            <div className="rounded-circle bg-success bg-opacity-10 p-2 me-2">
                                                                <i className="fas fa-balance-scale text-success"></i>
                                                            </div>
                                                            <span className="small">Ws/Wr Analysis</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 col-6">
                                                        <div className="d-flex align-items-center">
                                                            <div className="rounded-circle bg-warning bg-opacity-10 p-2 me-2">
                                                                <i className="fas fa-fish text-warning"></i>
                                                            </div>
                                                            <span className="small">Species Metrics</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 col-6">
                                                        <div className="d-flex align-items-center">
                                                            <div className="rounded-circle bg-info bg-opacity-10 p-2 me-2">
                                                                <i className="fas fa-file-export text-info"></i>
                                                            </div>
                                                            <span className="small">Multi-format Export</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CustomModal>

                        {/* Excel Import Modal */}
                        <CustomModal
                            isOpen={isImportModalOpen}
                            onClose={handleImportModalClose}
                            onSubmit={handleImportSubmit}
                            title={
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <div className="d-flex align-items-center">
                                        <i className="fa fa-file-excel text-success me-2" style={{ fontSize: '24px' }}></i>
                                        <span>Import Fish Samples from Excel</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-link text-info p-0"
                                        onClick={() => setIsInfoModalOpen(true)}
                                        title="Show Requirements"
                                    >
                                        <i className="fas fa-info-circle fa-lg"></i>
                                    </button>
                                </div>
                            }
                            showButton={true}
                            showCancelButton={true}
                            showSubmitButton={true}
                            modalSize="modal-xl"
                            minHeight="auto"
                            submitLabel={
                                isImporting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin me-2"></i>
                                        Queuing...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-file-import me-2"></i>
                                        Import Data
                                    </>
                                )
                            }
                            onError={(error) => {
                                // Focus on error messages when there's an error
                                if (importErrors.length > 0) {
                                    setTimeout(() => {
                                        const errorElement = document.querySelector('.bg-danger');
                                        if (errorElement) {
                                            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        }
                                    }, 100);
                                }
                            }}
                            submitDisabled={isImporting || !importFile || isParsingFile}
                        >

                            {isImporting && (
                                <div
                                    className="position-fixed w-100 h-100 top-0 start-0 d-flex align-items-center justify-content-center"
                                    style={{
                                        zIndex: 2000,
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(2px)'
                                    }}
                                >
                                    <div className="text-center" style={{ maxWidth: '500px', width: '90%' }}>
                                        <div className="mb-4">
                                            <i className="fas fa-file-import text-primary" style={{ fontSize: '48px' }}></i>
                                        </div>
                                        <h4 className="mb-3 text-primary">Preparing background import...</h4>

                                        {/* Progress Bar */}
                                        <div className="progress mb-3" style={{ height: '10px' }}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                                                role="progressbar"
                                                style={{
                                                    width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%`,
                                                    transition: 'width 0.3s ease'
                                                }}
                                            ></div>
                                        </div>

                                        {/* Progress Text */}
                                        <div className="mb-3">
                                            <span className="badge bg-primary fs-6 px-3 py-2">
                                                {importProgress.current} of {importProgress.total} rows processed
                                            </span>
                                        </div>

                                        {/* Current Row Being Processed */}
                                        {importProgress.currentRow && (
                                            <div className="card shadow-sm mb-3">
                                                <div className="card-header bg-light">
                                                    <small className="text-muted">Currently Processing Row {importProgress.currentRow.index}</small>
                                                </div>
                                                <div className="card-body py-2">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="fw-bold">{importProgress.currentRow.FishTypeName}</span>
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                            <span className="visually-hidden">Processing...</span>
                                                        </div>
                                                    </div>
                                                    <small className="text-muted">
                                                        Length: {importProgress.currentRow.Length} | Weight: {importProgress.currentRow.Weight}
                                                    </small>
                                                </div>
                                            </div>
                                        )}

                                        {/* Processed Rows Summary */}
                                        <div className="row text-center">
                                            <div className="col-4">
                                                <div className="text-success">
                                                    <i className="fas fa-check-circle mb-1" style={{ fontSize: '20px' }}></i>
                                                    <div className="fw-bold">{processedRows.filter(r => r.status === 'success').length}</div>
                                                    <small>Success</small>
                                                </div>
                                            </div>
                                            <div className="col-4">
                                                <div className="text-danger">
                                                    <i className="fas fa-times-circle mb-1" style={{ fontSize: '20px' }}></i>
                                                    <div className="fw-bold">{processedRows.filter(r => r.status === 'error').length}</div>
                                                    <small>Errors</small>
                                                </div>
                                            </div>
                                            <div className="col-4">
                                                <div className="text-warning">
                                                    <i className="fas fa-exclamation-circle mb-1" style={{ fontSize: '20px' }}></i>
                                                    <div className="fw-bold">{processedRows.filter(r => r.status === 'skipped').length}</div>
                                                    <small>Skipped</small>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Live Row Processing List */}
                                        {processedRows.length > 0 && (
                                            <div className="mt-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                <div className="text-start">
                                                    <small className="text-muted mb-2 d-block">Recent Activity:</small>
                                                    {processedRows.slice(-5).reverse().map((row, index) => (
                                                        <div key={`${row.index}-${index}`} className="d-flex align-items-center mb-1 p-2 bg-light rounded">
                                                            <div className="me-2">
                                                                {row.status === 'success' && <i className="fas fa-check text-success"></i>}
                                                                {row.status === 'error' && <i className="fas fa-times text-danger"></i>}
                                                                {row.status === 'skipped' && <i className="fas fa-exclamation text-warning"></i>}
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <small>
                                                                    <strong>Row {row.index}:</strong> {row.data.FishTypeName}
                                                                    {row.status === 'error' && row.error && (
                                                                        <div className="text-danger" style={{ fontSize: '10px' }}>{row.error}</div>
                                                                    )}
                                                                    {row.status === 'skipped' && row.reason && (
                                                                        <div className="text-warning" style={{ fontSize: '10px' }}>{row.reason}</div>
                                                                    )}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="mt-3" style={{ position: 'relative' }}>
                                {/* Simple loading indicator as backup */}
                                {isImporting && (
                                    <div className="alert alert-info text-center mb-3">
                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                        <strong>Queuing your Excel import… we will email you when it finishes.</strong>
                                    </div>
                                )}

                                <div className="row">
                                    {/* Step 1: Download Template */}
                                    <div className="col-md-6 mb-3">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-header bg-primary text-white">
                                                <i className="fas fa-download me-2"></i>
                                                Step 1: Get Template
                                            </div>
                                            <div className="card-body">
                                                <p className="card-text">
                                                    Download a template for this session ({normalizeSamplingMethod(pond?.SamplingMethod) || "sampling method"}).
                                                    Required columns:{" "}
                                                    <strong>
                                                        {getExcelImportColumnSpec(assignmentMode)
                                                            .filter((col) => col.required)
                                                            .map((col) => col.header)
                                                            .join(", ")}
                                                    </strong>
                                                    {assignmentMode === "effort_unit" && " (+ EffortUnit from Effort Units sheet)"}
                                                    {assignmentMode === "angler" && " (+ Angler from Anglers sheet)"}
                                                </p>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary w-100"
                                                    onClick={generateExcelTemplate}
                                                >
                                                    <i className="fas fa-file-download me-2"></i>
                                                    Download Template
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Step 2: Upload File */}
                                    <div className="col-md-6 mb-3">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-header bg-success text-white">
                                                <i className="fas fa-upload me-2"></i>
                                                Step 2: Upload Data
                                            </div>
                                            <div className="card-body">
                                                <p className="card-text">Select your filled Excel file to import.</p>
                                                <div
                                                    className="upload-zone p-3 bg-light rounded text-center"
                                                    style={{ border: '2px dashed #ccc' }}
                                                >
                                                    <input
                                                        type="file"
                                                        className="d-none"
                                                        id="excelFileInput"
                                                        accept=".xlsx,.xls"
                                                        onChange={handleImportFileChange}
                                                        disabled={isImporting || isParsingFile}
                                                    />
                                                    <label
                                                        htmlFor="excelFileInput"
                                                        className="mb-0"
                                                        style={{ cursor: isParsingFile ? "wait" : "pointer" }}
                                                    >
                                                        <div className="text-muted">
                                                            {isParsingFile ? (
                                                                <>
                                                                    <div className="spinner-border spinner-border-sm text-success mb-2" role="status"></div>
                                                                    <p className="mb-0">Reading Excel file...</p>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="fas fa-cloud-upload-alt fa-2x mb-2"></i>
                                                                    <p className="mb-0">Click to select file or drag and drop</p>
                                                                    <small>(.xlsx or .xls)</small>
                                                                </>
                                                            )}
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Selected File Info */}
                                {importFile && (
                                    <div className="card border-0 shadow-sm mb-3">
                                        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center">
                                                <i className="fas fa-file-excel me-2"></i>
                                                <span>{importFile.name}</span>
                                            </div>
                                        </div>
                                        {showPreview && (
                                            <div className="card-body p-0">
                                                {previewTotalRows > previewData.length && (
                                                    <div className="px-3 py-2 text-muted small border-bottom">
                                                        Showing first {previewData.length} of {previewTotalRows} fish rows.
                                                    </div>
                                                )}
                                                <div className="table-responsive">
                                                    <table className="table table-hover mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Fish Type</th>
                                                                <th>Length ({lengthUnit})</th>
                                                                <th>Weight ({weightUnit})</th>
                                                                <th>Tag Number</th>
                                                                <th>{assignmentLabel}</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {previewData.map((row, index) => (
                                                                <tr key={index}>
                                                                    <td>{row.FishTypeName}</td>
                                                                    <td>{row.Length}</td>
                                                                    <td>{row.Weight}</td>
                                                                    <td>{row.TagNumber || '-'}</td>
                                                                    <td>{row._resolvedAssignment ?? "—"}</td>
                                                                    <td>
                                                                        <span className={`badge ${row.Status === 'Culled' ? 'bg-success' : row.Status === 'Released' ? 'bg-warning' : 'bg-secondary'}`}>
                                                                            {row.Status || 'Not Set'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Validation Errors */}
                                {importErrors.length > 0 && (
                                    <div className="card border-0 shadow-sm mb-3 bg-danger text-white">
                                        <div className="card-header bg-danger text-white border-bottom-0">
                                            <i className="fas fa-exclamation-triangle me-2"></i>
                                            Validation Errors
                                        </div>
                                        <div className="card-body">
                                            <ul className="list-unstyled mb-0">
                                                {importErrors.map((error, index) => (
                                                    <li key={index}>
                                                        <i className="fas fa-times-circle me-2"></i>
                                                        {error}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CustomModal>

                        {/* Requirements Info Modal */}
                        <CustomModal
                            isOpen={isInfoModalOpen}
                            onClose={() => setIsInfoModalOpen(false)}
                            title={
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-info-circle text-info me-2"></i>
                                    <span>Import Requirements</span>
                                </div>
                            }
                            showButton={true}
                            showCancelButton={false}
                            showSubmitButton={false}
                            modalSize="modal-lg"
                        >
                            <div className="mt-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <h6 className="mb-3">Required Columns:</h6>
                                                <ul className="list-unstyled">
                                                    {getExcelImportColumnSpec(assignmentMode).map((col) => (
                                                        <li key={col.key} className="mb-2">
                                                            <i
                                                                className={`fas ${
                                                                    col.required
                                                                        ? "fa-check text-success"
                                                                        : "fa-minus text-muted"
                                                                } me-2`}
                                                            ></i>
                                                            <strong>{col.header}</strong>
                                                            {col.required && (
                                                                <span className="badge bg-danger ms-1">Required</span>
                                                            )}
                                                            <div className="text-muted small ms-4">{col.description}</div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="col-md-6">
                                                <h6 className="mb-3">Validation Rules:</h6>
                                                <ul className="list-unstyled">
                                                    <li className="mb-2">
                                                        <i className="fas fa-info-circle text-info me-2"></i>
                                                        Length must be positive number
                                                    </li>
                                                    <li className="mb-2">
                                                        <i className="fas fa-info-circle text-info me-2"></i>
                                                        Weight must be positive number
                                                    </li>
                                                    <li className="mb-2">
                                                        <i className="fas fa-info-circle text-info me-2"></i>
                                                        Fish names are case-insensitive (will match regardless of case)
                                                    </li>
                                                    <li className="mb-2">
                                                        <i className="fas fa-info-circle text-info me-2"></i>
                                                        Status values are case-sensitive (optional)
                                                    </li>
                                                    <li className="mb-2">
                                                        <i className="fas fa-info-circle text-info me-2"></i>
                                                        Delete the example row in the template before importing
                                                    </li>
                                                    <li className="mb-2">
                                                        <i className="fas fa-info-circle text-info me-2"></i>
                                                        Use the <strong>Sampling Data</strong> sheet for fish rows
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CustomModal>
                    </div>
                </div>
            </div>
            </div>

    );
};

export default SamplingDetailViaPondDetail;